import { AUDIT_OBJECT_TYPES, type AuditEventDto } from '@time-tracking/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AuditLogFilters } from '../../../api/admin.js';
import { useAuditLog } from '../../../api/admin.js';
import '../audit-log.css';

// ── Types ─────────────────────────────────────────────────────────────────────

type Tone = 'ok' | 'warn' | 'danger' | 'create' | 'update' | 'lock' | 'system' | 'default';

// ── Tone helpers ──────────────────────────────────────────────────────────────

function eventTone(eventType: string): Tone {
  if (eventType === 'TIMESHEET_APPROVED') return 'ok';
  if (
    eventType === 'TIMESHEET_RETURNED' ||
    eventType === 'TIMESHEET_UNAPPROVED' ||
    eventType === 'TIMESHEET_RECALLED' ||
    // Un-finalises a reported month, so it reads as a caution, not a routine edit.
    eventType === 'REPORTING_PERIOD_REOPENED' ||
    eventType === 'ASSIGNMENT_REMOVED' ||
    // USER_DEACTIVATED was never emitted; the admin action is USER_ACTIVE_CHANGED.
    eventType === 'USER_ACTIVE_CHANGED' ||
    eventType === 'USER_ACTIVE_SYNCED'
  )
    return 'warn';
  if (
    eventType === 'TIME_ENTRY_DELETED' ||
    eventType === 'LINE_CLASSIFICATION_REMOVED' ||
    eventType === 'TEAM_REMOVED_FROM_TASK' ||
    eventType.endsWith('_DELETED') ||
    eventType.endsWith('_REMOVED')
  )
    return 'danger';
  if (
    eventType === 'TIMESHEET_CREATED' ||
    eventType === 'TIMESHEET_LINE_CREATED' ||
    eventType === 'ASSIGNMENT_CREATED' ||
    eventType === 'LINE_CLASSIFICATION_CREATED' ||
    eventType === 'TEAM_ASSIGNED_TO_TASK' ||
    eventType.endsWith('_CREATED') ||
    eventType.endsWith('_ASSIGNED')
  )
    return 'create';
  if (
    eventType === 'PROJECT_LOCKED' ||
    eventType === 'TASK_LOCKED' ||
    // Closing a period locks its weeks; no `_LOCKED` suffix, so named here.
    eventType === 'REPORTING_PERIOD_CLOSED' ||
    eventType.endsWith('_LOCKED')
  )
    return 'lock';
  // System-provenance events: a sync or import run recorded as a whole, not a
  // single action a person took in the UI, so they share the system tone.
  // CATALOG_IMPORT_RUN carries the operator as actor, but is still a run summary.
  if (
    eventType === 'AD_SYNC_COMPLETED' ||
    eventType === 'TEAM_STRUCTURE_IMPORTED' ||
    eventType === 'CATALOG_IMPORT_RUN'
  )
    return 'system';
  if (
    eventType.endsWith('_UPDATED') ||
    eventType === 'ROLE_CHANGED' ||
    eventType === 'USER_ROLE_SYNCED' ||
    eventType === 'TASK_ARCHIVED' ||
    eventType === 'TIMESHEET_RESUBMITTED'
  )
    return 'update';
  return 'default';
}

function formatEventLabel(eventType: string): string {
  return eventType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Day grouping helpers ───────────────────────────────────────────────────────

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

// ── localStorage helpers (for "Recent" actors) ────────────────────────────────

const RECENT_ACTORS_KEY = 'audit.recentActors.v1';

function loadRecentActors(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_ACTORS_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

function pushRecentActor(id: string): void {
  let list = loadRecentActors().filter((x) => x !== id);
  list.unshift(id);
  list = list.slice(0, 5);
  try {
    localStorage.setItem(RECENT_ACTORS_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ── ActorFilter component ─────────────────────────────────────────────────────

interface ActorEntry {
  name: string;
  /** Server-computed (see AuditEventDto.actor); rendered verbatim. */
  initials: string;
  role: string | null;
}

interface ActorFilterProps {
  actorDir: Map<string, ActorEntry>;
  eventCounts: Record<string, number>;
  selected: string[];
  onChange: (sel: string[]) => void;
}

function ActorFilter({ actorDir, eventCounts, selected, onChange }: ActorFilterProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const allActorIds = useMemo(
    () =>
      [...actorDir.keys()].sort((a, b) =>
        (actorDir.get(a)?.name ?? '').localeCompare(actorDir.get(b)?.name ?? ''),
      ),
    [actorDir],
  );

  // Include `selected` in deps so the list refreshes after each selection
  // (pushRecentActor updates localStorage; re-rendering on selection change picks it up).
  const recent = useMemo(
    () => loadRecentActors().filter((id) => actorDir.has(id)),
    [open, selected],
  );

  const frequent = useMemo(
    () =>
      Object.entries(eventCounts)
        .sort(([, a], [, b]) => b - a)
        .map(([id]) => id)
        .filter((id) => actorDir.has(id)),
    [eventCounts, actorDir],
  );

  const query = q.trim().toLowerCase();
  const browsing = !!query || roleFilter !== 'all';

  const matches = useMemo(
    () =>
      allActorIds.filter((id) => {
        const a = actorDir.get(id);
        if (!a) return false;
        if (roleFilter !== 'all' && (a.role ?? '').toLowerCase() !== roleFilter.toLowerCase())
          return false;
        if (!query) return true;
        return (a.name + ' ' + id).toLowerCase().includes(query);
      }),
    [allActorIds, query, roleFilter, actorDir],
  );

  const freqShown = frequent.filter((id) => !recent.includes(id)).slice(0, 6);

  function toggle(id: string) {
    const isSel = selected.includes(id);
    const next = isSel ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
    if (!isSel) pushRecentActor(id);
  }

  let label: string;
  if (selected.length === 0) label = 'All actors';
  else if (selected.length === 1) {
    const firstId = selected[0] ?? '';
    label = actorDir.get(firstId)?.name ?? firstId;
  } else label = `${selected.length} actors`;

  // SVG helpers (inline — keep small)
  const ChevDownSvg = (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
  const XSvg = (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
  const SearchSmSvg = (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
  const CheckSvg = (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
  const ClockSvg = (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
  const SparkSvg = (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" />
    </svg>
  );

  function roleCls(role: string | null): string {
    if (role === 'Administrator' || role === 'administrator') return 'admin';
    if (role === 'Approver' || role === 'approver') return 'appr';
    return 'mem';
  }
  function roleShort(role: string | null): string {
    if (role === 'Administrator' || role === 'administrator') return 'Admin';
    if (role === 'Approver' || role === 'approver') return 'Approver';
    return 'Member';
  }

  function ActorRow({ id }: { id: string }) {
    const a = actorDir.get(id);
    if (!a) return null;
    const isSel = selected.includes(id);
    return (
      <button
        type="button"
        className={'acf-row' + (isSel ? ' sel' : '')}
        onClick={() => toggle(id)}
        aria-pressed={isSel}
      >
        <span className="acf-check" aria-hidden="true">
          {isSel && CheckSvg}
        </span>
        <span className="aud-avatar" aria-hidden="true">
          {a.initials}
        </span>
        <span className="acf-meta">
          <span className="acf-name">{a.name}</span>
          <span className="acf-sub">{id}</span>
        </span>
        {a.role && <span className={'acf-role ' + roleCls(a.role)}>{roleShort(a.role)}</span>}
        {eventCounts[id] != null && (
          <span className="acf-count" title={`${eventCounts[id]} events`}>
            {eventCounts[id]}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="aud-filter acf" ref={rootRef}>
      <span>Actor</span>
      <div className="acf-trigger-wrap">
        <button
          type="button"
          className={'acf-trigger' + (selected.length > 0 ? ' filled' : '')}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <span className="acf-trigger-label">{label}</span>
          {selected.length === 0 && ChevDownSvg}
        </button>
        {selected.length > 0 && (
          <button
            type="button"
            className="acf-trigger-x"
            aria-label="Clear actor filter"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          >
            {XSvg}
          </button>
        )}
      </div>

      {open && (
        <div className="acf-pop" role="dialog" aria-label="Filter by actor">
          <div className="acf-search">
            <span className="icon">{SearchSmSvg}</span>
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or EID…"
              aria-label="Search actors by name or EID"
            />
            {q && (
              <button
                type="button"
                className="acf-clear"
                onClick={() => setQ('')}
                aria-label="Clear search"
              >
                {XSvg}
              </button>
            )}
          </div>

          <div className="acf-roles">
            {(['all', 'Administrator', 'Approver', 'Contributor'] as const).map((r) => (
              <button
                key={r}
                type="button"
                className={'acf-role-chip' + (roleFilter === r ? ' on' : '')}
                onClick={() => setRoleFilter(r)}
                aria-pressed={roleFilter === r}
              >
                {r === 'all'
                  ? 'All roles'
                  : r === 'Administrator'
                    ? 'Admins'
                    : r === 'Approver'
                      ? 'Approvers'
                      : 'Members'}
              </button>
            ))}
          </div>

          {selected.length > 0 && (
            <div className="acf-selected">
              {selected.map((id) => (
                <span key={id} className="acf-chip">
                  {actorDir.get(id)?.name ?? id}
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    aria-label={`Remove ${actorDir.get(id)?.name ?? id} from filter`}
                  >
                    {XSvg}
                  </button>
                </span>
              ))}
              <button type="button" className="acf-clearall" onClick={() => onChange([])}>
                Clear all
              </button>
            </div>
          )}

          <div className="acf-list">
            {!browsing ? (
              <>
                {recent.length > 0 && (
                  <>
                    <div className="acf-grouphd">{ClockSvg} Recent</div>
                    {recent.map((id) => (
                      <ActorRow key={'r' + id} id={id} />
                    ))}
                  </>
                )}
                <div className="acf-grouphd">{SparkSvg} Most active</div>
                {freqShown.map((id) => (
                  <ActorRow key={'f' + id} id={id} />
                ))}
                <div className="acf-hint">
                  Type a name or EID to search all {actorDir.size} users.
                </div>
              </>
            ) : matches.length === 0 ? (
              <div className="acf-empty">No users match &ldquo;{q}&rdquo;.</div>
            ) : (
              <>
                {matches.slice(0, 60).map((id) => (
                  <ActorRow key={id} id={id} />
                ))}
                {matches.length > 60 && (
                  <div className="acf-hint">
                    Showing 60 of {matches.length}. Keep typing to narrow.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Event row ─────────────────────────────────────────────────────────────────

interface EventRowProps {
  event: AuditEventDto;
  expanded: boolean;
  onToggle: () => void;
}

function EventRow({ event, expanded, onToggle }: EventRowProps) {
  const tone = eventTone(event.eventType);
  const hasDiff = event.beforeJson !== null || event.afterJson !== null;
  const hasDetail = event.detail !== null;
  const hasReason = event.reason !== null;
  const hasExpand = hasDiff || hasDetail || hasReason;

  // Fix 2 — wrapper div .audit-row, button .audit-main
  return (
    <div className={`audit-row${expanded ? ' open' : ''}`}>
      <button
        type="button"
        className="audit-main"
        aria-expanded={expanded}
        onClick={onToggle}
        data-testid="audit-row"
      >
        {/* Fix 1 — caret first */}
        <span className="aud-caret" aria-hidden="true">
          <ChevronIcon />
        </span>

        {/* Fix 3 — .aud-time */}
        <span className="aud-time" title={event.createdAt}>
          {relativeTime(event.createdAt)}
        </span>

        {/* Fix 3 — .aud-actor with avatar + name */}
        <span className="aud-actor">
          <span className="aud-avatar" aria-hidden="true">
            {event.actor.initials}
          </span>
          <span className="aud-actor-name">{event.actor.displayName}</span>
        </span>

        <span className={`ev-tag tone-${tone}`} data-testid="audit-event-tag" data-tone={tone}>
          {formatEventLabel(event.eventType)}
        </span>

        {/* Fix 4 — .aud-obj wrapper with obj-chip + aud-obj-name */}
        <span className="aud-obj">
          <span className="obj-chip" data-testid="audit-object-chip">
            {event.objectType}
          </span>
          {event.objectName && (
            <span className="aud-obj-name" title={event.objectName}>
              {event.objectName}
            </span>
          )}
        </span>
      </button>

      {/* Fix 2 — expand panel class: audit-detail */}
      {expanded && hasExpand && (
        <div className="audit-detail">
          {/* Fix 5 — .aud-diff with text → arrow */}
          {hasDiff && (
            <div className="aud-diff">
              <div className="aud-diff-col before">
                <div className="lbl">Before</div>
                <div className="val">
                  {event.beforeJson !== null ? (
                    JSON.stringify(event.beforeJson, null, 2)
                  ) : (
                    <span className="none">—</span>
                  )}
                </div>
              </div>
              <span className="aud-arrow">→</span>
              <div className="aud-diff-col after">
                <div className="lbl">After</div>
                <div className="val">
                  {event.afterJson !== null ? (
                    JSON.stringify(event.afterJson, null, 2)
                  ) : (
                    <span className="none">—</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {hasDetail && (
            <div className="aud-line">
              <span className="k">Detail</span>
              {event.detail}
            </div>
          )}

          {hasReason && (
            <div className="aud-line">
              <span className="k">Reason</span>
              {event.reason}
            </div>
          )}

          <div className="aud-meta">
            <span className="aud-meta-item">
              {new Date(event.createdAt).toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'long',
              })}
            </span>
            {event.actorRole && <span className="aud-meta-item">{event.actorRole}</span>}
            {event.correlationId && (
              <span className="aud-meta-item cid">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                {event.correlationId}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main pane ─────────────────────────────────────────────────────────────────

// Driven by the same array the API validates against, so the dropdown cannot
// drift out of sync with what the filter accepts. The previous hand-maintained
// copy omitted admin_category, user and team_membership.
const OBJECT_TYPES: readonly string[] = AUDIT_OBJECT_TYPES;

export function AuditLogPane() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actorSel, setActorSel] = useState<string[]>([]);
  const [objectType, setObjectType] = useState('');
  const [range, setRange] = useState<'24h' | '7d' | '30d' | 'all'>('30d');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the export toast timer on unmount to prevent state updates after unmount.
  useEffect(() => {
    return () => {
      if (toastTimerRef.current !== null) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Debounce search by 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Push a single actor to the server so the API total reflects the full audit log, not
  // just the currently loaded page. Multi-actor stays client-side.
  const serverActor = actorSel.length === 1 ? actorSel[0] : undefined;

  const filters: AuditLogFilters = {
    limit: 200,
    range,
    ...(objectType ? { objectType } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(serverActor ? { actor: serverActor } : {}),
  };

  const { data, isLoading, isError } = useAuditLog(filters);

  const events = data?.events ?? [];

  // Build actor directory and event counts from loaded events
  const actorDir = useMemo(() => {
    const dir = new Map<string, ActorEntry>();
    for (const e of events) {
      if (!dir.has(e.actorUserId)) {
        dir.set(e.actorUserId, {
          name: e.actor.displayName,
          initials: e.actor.initials,
          role: e.actorRole,
        });
      }
    }
    return dir;
  }, [events]);

  const actorEventCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of events) {
      counts[e.actorUserId] = (counts[e.actorUserId] ?? 0) + 1;
    }
    return counts;
  }, [events]);

  // Single actor pushed to server — no client-side filtering needed.
  // Multi-actor selection (2+) remains client-side since the API only accepts one actor.
  const filteredEvents = useMemo(
    () =>
      actorSel.length === 0 || serverActor
        ? events
        : events.filter((e) => actorSel.includes(e.actorUserId)),
    [events, actorSel, serverActor],
  );

  // Group events by calendar day
  const grouped: { key: string; label: string; events: AuditEventDto[] }[] = [];
  let currentKey = '';
  let currentGroup: AuditEventDto[] = [];

  for (const event of filteredEvents) {
    const key = dayKey(event.createdAt);
    if (key !== currentKey) {
      if (currentGroup.length > 0) {
        const firstEvent = currentGroup[0];
        grouped.push({
          key: currentKey,
          label: firstEvent ? dayLabel(firstEvent.createdAt) : '',
          events: currentGroup,
        });
      }
      currentKey = key;
      currentGroup = [event];
    } else {
      currentGroup.push(event);
    }
  }
  if (currentGroup.length > 0) {
    const firstEvent = currentGroup[0];
    grouped.push({
      key: currentKey,
      label: firstEvent ? dayLabel(firstEvent.createdAt) : '',
      events: currentGroup,
    });
  }

  // Use the API total when no client-side filtering is in effect (single actor was pushed
  // to server, or no actor filter at all). Fall back to filteredEvents.length for multi-
  // actor client-side filtering where the API total doesn't reflect the active selection.
  const isClientSideFiltered = actorSel.length > 1;
  const total = isClientSideFiltered
    ? filteredEvents.length
    : (data?.total ?? filteredEvents.length);

  const toggleExpand = useCallback((eventId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  function handleExport() {
    // Clear to blank first so the aria-live region re-announces on repeated clicks.
    setToastVisible(false);
    if (toastTimerRef.current !== null) clearTimeout(toastTimerRef.current);
    // Defer the re-set by a tick so browsers detect the DOM change.
    toastTimerRef.current = setTimeout(() => {
      setToastVisible(true);
      toastTimerRef.current = setTimeout(() => {
        setToastVisible(false);
        toastTimerRef.current = null;
      }, 4000);
    }, 0);
  }

  return (
    <div className="audit-pane">
      <div className="audit-toolbar">
        <div className="audit-search">
          <span className="icon">
            <SearchIcon />
          </span>
          <input
            type="text"
            placeholder="Search actor, action, or object…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search audit events"
          />
        </div>

        <ActorFilter
          actorDir={actorDir}
          eventCounts={actorEventCounts}
          selected={actorSel}
          onChange={setActorSel}
        />

        <label className="aud-filter" htmlFor="audit-type-filter">
          <span>Object</span>
          <select
            id="audit-type-filter"
            value={objectType}
            onChange={(e) => setObjectType(e.target.value)}
            data-testid="audit-object-type"
          >
            <option value="">All objects</option>
            {OBJECT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>

        <label className="aud-filter" htmlFor="audit-range">
          <span>Range</span>
          <select
            id="audit-range"
            value={range}
            onChange={(e) => setRange(e.target.value as typeof range)}
            data-testid="audit-range"
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </label>

        {/* Fix 6 — stat-chip + Export in .right div */}
        <div className="right">
          {!isLoading && !isError && (
            <span className="stat-chip" aria-live="polite">
              {total.toLocaleString()} event{total !== 1 ? 's' : ''}
            </span>
          )}
          <button type="button" className="btn btn--sm btn--ghost" onClick={handleExport}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Toast notification */}
      {toastVisible && (
        <div role="status" aria-live="polite" className="audit-toast">
          Export coming soon — check back after the exports feature lands.
        </div>
      )}

      {isLoading && (
        <div className="audit-status" aria-live="polite">
          Loading…
        </div>
      )}

      {isError && (
        <div className="audit-status error" role="alert">
          Failed to load audit log.
        </div>
      )}

      <div aria-live="polite" aria-atomic="true">
        {!isLoading && !isError && filteredEvents.length === 0 && (
          <div className="audit-empty">
            <div className="ic">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h3>No events match your filters</h3>
            <p>Try widening the date range or clearing the search.</p>
          </div>
        )}
      </div>

      {!isLoading && !isError && filteredEvents.length > 0 && (
        <div className="audit-log">
          {grouped.map((group) => (
            <div key={group.key}>
              {/* Fix 10 — audit-day (was audit-day-header) */}
              <h3 className="audit-day">{group.label}</h3>
              {group.events.map((event) => (
                <EventRow
                  key={event.eventId}
                  event={event}
                  expanded={expandedIds.has(event.eventId)}
                  onToggle={() => toggleExpand(event.eventId)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
